import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  MenuItem,
  ListItemText,
  Box,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import { getDepartmentsByLocation, getEmployeeAttendanceTrend } from "src/api/faceRegistration";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { getURL } from "src/utils/config";
import axios from "axios";
import Page from "src/components/Page";
import { LoadingButton } from '@mui/lab';

const EMPTY_CELL_VALUE = "N/A";
const OUTLINED_BORDER_COLOR = "rgba(0, 0, 0, 0.23)";
const FILTER_CONTROL_WIDTH = 220;
const INITIAL_VISIBLE_ROWS = 20;
const ROW_LOAD_BATCH_SIZE = 20;
const SCROLL_LOAD_THRESHOLD_PX = 80;

const normalizeDepartmentOptions = (departments) =>
  (Array.isArray(departments) ? departments : (departments ? [departments] : []))
    .map((departmentItem, index) => ({
      ...departmentItem,
      Id: departmentItem?.Id ??
        departmentItem?.ID ??
        departmentItem?.id ??
        departmentItem?.DepartmentId ??
        departmentItem?.DepartmentID ??
        departmentItem?.department_id ??
        `department-${index}`,
      DepartmentName: String(
        departmentItem?.DepartmentName ??
        departmentItem?.departmentName ??
        departmentItem?.Department ??
        departmentItem?.department ??
        departmentItem?.Name ??
        departmentItem?.name ??
        ''
      ).trim(),
    }))
    .filter((departmentItem) => departmentItem.DepartmentName);

const formatPercentageValue = (item, fieldNames) => {
  const value = getFirstFieldValue(item, fieldNames, "");

  if (value === "") {
    return EMPTY_CELL_VALUE;
  }

  return `${value}%`;
};

const normalizeAttendanceTrendResponse = (data) => (
  Array.isArray(data) ? data : (data ? [data] : [])
);

// Table columns for main page
const attendanceTrendColumns = [
  {
    id: "location",
    label: "Location",
    width: 20,
    getValue: (item) => getFirstFieldValue(item, [
      "Location",
      "location",
      "LocationName",
      "locationName"
    ])
  },
  {
    id: "department",
    label: "Department",
    width: 25,
    getValue: (item) => getFirstFieldValue(item, [
      "Department",
      "department",
      "DepartmentName",
      "departmentName"
    ])
  },
  {
    id: "attendancePercentage",
    label: "Attendance Percentage",
    width: 18,
    getValue: (item) => formatPercentageValue(item, [
      "AttendancePercentage",
      "attendancePercentage"
    ])
  },
  {
    id: "absenteeismPercentage",
    label: "Absenteeism Percentage",
    width: 18,
    getValue: (item) => formatPercentageValue(item, [
      "AbsenteeismPercentage",
      "absenteeismPercentage"
    ])
  }
];

const getFirstFieldValue = (item, fieldNames, fallback = EMPTY_CELL_VALUE) => {
  const value = fieldNames
    .map((fieldName) => item?.[fieldName])
    .find((fieldValue) => (
      fieldValue !== null &&
      fieldValue !== undefined &&
      String(fieldValue).trim() !== ""
    ));

  return value === undefined ? fallback : value;
};

const AttendanceTrend = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [visibleRowCount, setVisibleRowCount] = useState(INITIAL_VISIBLE_ROWS);
  const [apiData, setApiData] = useState(null);
  const [locationNames, setLocationNames] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [departmentError, setDepartmentError] = useState('');
  const departmentRequestIdRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [monthError, setMonthError] = useState(false);
  const [yearError, setYearError] = useState(false);
  
  // Month and Year states
  const [selectedMonth, setSelectedMonth] = useState();
  const [selectedYear, setSelectedYear] = useState();
  
  // Generate year options (current, next, next to next)
  const currentYear = dayjs().year();
  const yearOptions = [currentYear, currentYear + 1, currentYear + 2];
  
  // Generate month options
  const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    setMonthError(false);
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    setYearError(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      const apiUrl = getURL("employee-shifts-info/location-names?status=1");
      const AUTH_TOKEN = `${localStorage.getItem('token_type')} ${localStorage.getItem('access_token')}`;
      
      try {
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: AUTH_TOKEN,
          },
        });

        const extractedLocationNames = response.data.map(item => item.LocationName);
        setLocationNames(extractedLocationNames);
        setApiData(response.data);
      } catch (error) {
        console.error('Error:', error);
      }
    };
  
    fetchData();
  }, []);

  const updateLocation = useCallback((value) => {
    setSelectedLocation(value);
    const selectedLocationObject = apiData?.find((location) => location.LocationName === value);
    const locationId = selectedLocationObject ? selectedLocationObject.Id : null;
    setSelectedLocationId(locationId);
    if (value) {
      localStorage.setItem("NetLocation", value);
    } else {
      localStorage.removeItem("NetLocation");
    }
    setLocationError(false);
    
    setSelectedDepartments([]);
    setDepartmentOptions([]);
    setDepartmentError('');

    const requestId = departmentRequestIdRef.current + 1;
    departmentRequestIdRef.current = requestId;

    if (!locationId) {
      setDepartmentLoading(false);
      return;
    }

    setDepartmentLoading(true);
    getDepartmentsByLocation({ location_id: locationId }, (response) => {
      if (departmentRequestIdRef.current !== requestId) return;

      if (response.status === 200) {
        setDepartmentOptions(normalizeDepartmentOptions(response.data));
        setDepartmentError('');
      } else {
        setDepartmentOptions([]);
        setDepartmentError('Unable to load departments');
      }
      setDepartmentLoading(false);
    });
  }, [apiData]);

  useEffect(() => {
    const savedLocation = localStorage.getItem("NetLocation");
    // if (savedLocation && apiData) {
    //   updateLocation(savedLocation);
    // }
  }, [apiData, updateLocation]);

  const handleLocationChange = (value) => {
    updateLocation(value);
  };

  const handleDepartmentChange = (_event, value) => {
    if (!selectedLocation || departmentLoading) {
      setSelectedDepartments([]);
      return;
    }
    if (value.some((option) => option.isSelectAll)) {
      handleSelectAllDepartments();
      return;
    }
    setSelectedDepartments(value);
  };

  const handleSelectAllDepartments = () => {
    if (!departmentOptions.length) {
      setSelectedDepartments([]);
      return;
    }
    if (selectedDepartments.length === departmentOptions.length) {
      setSelectedDepartments([]);
    } else {
      setSelectedDepartments(departmentOptions);
    }
  };

  const handleSubmit = () => {
    const locationId = selectedLocationId || 0;

    setLoading(true);
    setLocationError(false);
    setMonthError(false);
    setYearError(false);

    if (locationId === 0 || !selectedMonth || !selectedYear) {
      if (locationId === 0) {
        setLocationError(true);
      }
      if (!selectedMonth) {
        setMonthError(true);
      }
      if (!selectedYear) {
        setYearError(true);
      }
      setLoading(false);
      return;
    }

    const departmentIds = selectedDepartments.map(dept => dept.Id);

    const data = {
      location_id: locationId,
      month: selectedMonth.value || selectedMonth,
      year: selectedYear,
      department_id: departmentIds.join(','),
    };
    
    console.log('Submitting with data:', data);
    
    getEmployeeAttendanceTrend(data, (response) => {
      console.log('Full API Response:', response);
      
      if (response.status === 200) {
        if(response.data.length>0){
         
          const monthName = monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth.label;
          const data = response.data[0][monthName];
          const responseData = normalizeAttendanceTrendResponse(data);
          console.log('Response data:', responseData);
          setFilteredData(responseData);
          setVisibleRowCount(INITIAL_VISIBLE_ROWS);
        }      
      } else {
        setFilteredData([]);
        setVisibleRowCount(INITIAL_VISIBLE_ROWS);
      }
      setLoading(false);
    });
  };

  const handleExportExcel = async () => {
    const exportData = filteredData;
    const monthName = monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth;
    const fileName = `Attendance_Trend_${monthName}_${selectedYear}.xlsx`;

    if (exportData.length !== 0) {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Attendance Trend");
      
      // Define headers with styles
      worksheet.columns = attendanceTrendColumns.map((column) => ({
        header: column.label,
        key: column.id,
        width: column.width,
        alignment: { horizontal: "center" },
      }));

      // Add rows with data
      exportData.forEach((item, index) => {
        const rowData = attendanceTrendColumns.reduce((acc, column) => ({
          ...acc,
          [column.id]: column.getValue(item, index)
        }), {});
        const row = worksheet.addRow(rowData);

        row.eachCell((cell) => {
          cell.alignment = { horizontal: "center" };
        });
      });

      // Apply styles to headers
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center" };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
    } else {
      alert("No data to export.");
    }
  };

  const visibleData = filteredData.slice(0, visibleRowCount);

  const handleTableScroll = useCallback((event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight <= SCROLL_LOAD_THRESHOLD_PX;

    if (!isNearBottom) {
      return;
    }

    setVisibleRowCount((currentCount) => (
      Math.min(currentCount + ROW_LOAD_BATCH_SIZE, filteredData.length)
    ));
  }, [filteredData.length]);

  const filterControlSx = (hasError = false, width = FILTER_CONTROL_WIDTH) => ({
    width,
    minWidth: width,
    '& .MuiOutlinedInput-root': {
      minHeight: 56,
      backgroundColor: 'background.paper',
      '& fieldset': {
        borderColor: hasError ? 'error.main' : OUTLINED_BORDER_COLOR,
      },
      '&:hover fieldset': {
        borderColor: hasError ? 'error.main' : 'text.primary',
      },
      '&.Mui-focused fieldset': {
        borderColor: hasError ? 'error.main' : 'primary.main',
      },
      '&.Mui-disabled fieldset': {
        borderColor: hasError ? 'error.main' : OUTLINED_BORDER_COLOR,
      },
    },
    '& .MuiFormHelperText-root': {
      minHeight: 20,
      marginLeft: 0,
      marginRight: 0,
    },
    '& .MuiInputLabel-root': {
      backgroundColor: 'background.paper',
      px: 0.5,
    },
  });

  return (
    <Page title="Attendance Trend Report">
      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              flexWrap: 'nowrap',
              gap: 2,
              mx: 2,
              mt: 2,
              mb: 2,
              overflowX: 'auto',
              pt: 1.5,
              pb: 1,
            }}
          >
            {/* Year Dropdown */}
            <FormControl sx={filterControlSx(yearError)}>
              <Autocomplete
                value={selectedYear}
                onChange={(_event, value) => handleYearChange(value || null)}
                options={yearOptions}
                getOptionLabel={(option) => option?.toString() || ""}
                isOptionEqualToValue={(option, value) => option === value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Year"
                    variant="outlined"
                    error={yearError}
                    helperText={yearError ? "Please select Year" : " "}
                  />
                )}
                sx={filterControlSx(yearError)}
              />
            </FormControl>

            {/* Month Dropdown */}
            <FormControl sx={filterControlSx(monthError)}>
              <Autocomplete
                value={selectedMonth}
                onChange={(_event, value) => handleMonthChange(value || null)}
                options={monthOptions}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => option.value === value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Month"
                    variant="outlined"
                    error={monthError}
                    helperText={monthError ? "Please select Month" : " "}
                  />
                )}
                sx={filterControlSx(monthError)}
              />
            </FormControl>

            {/* Location Dropdown */}
            <FormControl sx={filterControlSx(locationError)}>
              <Autocomplete
                value={selectedLocation}
                onChange={(_event, value) => handleLocationChange(value || "")}
                options={locationNames}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Location"
                    variant="outlined"
                    error={locationError}
                    helperText={locationError ? "Please Select Location" : " "}
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                getOptionLabel={(option) => option || ""}
                noOptionsText="No locations available"
                sx={filterControlSx(locationError)}
              />
            </FormControl>

            {/* Department Dropdown */}
            <FormControl sx={filterControlSx(Boolean(departmentError))}>
              <Autocomplete
                multiple
                limitTags={1}
                renderTags={() => null}
                value={selectedDepartments}
                onChange={handleDepartmentChange}
                options={departmentOptions}
                loading={departmentLoading}
                loadingText="Loading departments..."
                noOptionsText={
                  selectedLocation
                    ? (departmentError || "No departments available")
                    : "Select location first"
                }
                getOptionLabel={(option) => option.DepartmentName || ""}
                isOptionEqualToValue={(option, value) => String(option.Id) === String(value.Id)}
                disabled={!selectedLocation || departmentLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Department"
                    variant="outlined"
                    error={Boolean(departmentError)}
                    helperText={departmentError || " "}
                    placeholder={
                      departmentLoading
                        ? "Loading departments..."
                        : !selectedLocation
                          ? "Select location first"
                          : "Select departments"
                    }
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <MenuItem {...props}>
                    <Checkbox
                      checked={
                        option.isSelectAll
                          ? departmentOptions.length > 0 && selectedDepartments.length === departmentOptions.length
                          : selected
                      }
                    />
                    <ListItemText primary={option.DepartmentName} />
                  </MenuItem>
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 200,
                  },
                }}
                componentsProps={{
                  popper: {
                    placement: 'bottom-start',
                  },
                }}
                sx={filterControlSx(Boolean(departmentError))}
                filterOptions={(options) => {
                  if (!selectedLocation || departmentLoading || options.length === 0) {
                    return options;
                  }
                  const selectAllOption = {
                    Id: -1,
                    DepartmentName: 'Select All',
                    isSelectAll: true,
                  };
                  return [selectAllOption, ...options];
                }}
              />
            </FormControl>

            {/* Submit and Export Buttons */}
            <Box sx={{ display: 'flex', gap: 1, minHeight: 56, alignItems: 'center', pt: 0 }}>
              <LoadingButton type="submit" variant="contained" onClick={handleSubmit} loading={loading}>
                Submit
              </LoadingButton>

              <LoadingButton type="submit" variant="contained" onClick={handleExportExcel}>
                Export
              </LoadingButton>
            </Box>
          </Box>

          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              onScroll={handleTableScroll}
              sx={{ minWidth: 1100, maxHeight: '65vh', overflow: 'auto' }}
            >
              <Table stickyHeader sx={{ minWidth: 1100 }}>
                <TableHead>
                  <TableRow>
                    {attendanceTrendColumns.map((column) => (
                      <TableCell key={column.id}>{column.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleData.map((item, index) => {
                    const absoluteIndex = index;

                    return (
                      <TableRow key={absoluteIndex}>
                        {attendanceTrendColumns.map((column) => {
                          const value = column.getValue(item, absoluteIndex);
                          
                          return (
                            <TableCell key={column.id}>
                              {value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {filteredData.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'right' }}>
              Showing {visibleData.length} of {filteredData.length}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Page>
  );
};

export default AttendanceTrend;
